import { ChangeEvent, useRef, useState } from 'react';
import { ChevronLeft, Plus, Search, Trash2, XCircle } from 'react-feather';
import { FormManager, useForm } from 'react-form-state-manager';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import {
  GetEventPickerEventsQuery as GetEventPickerEvents,
  GetEventRegistrationsStatsQuery as GetEventRegistrationsStats,
} from '__generated__/graphql';
import { useEvents } from '../Events/EventPicker';
import GetTicketListQuery from './GetTicketListQuery';
import UI from '../../common/UI';
import theme from '../../theme';
import useDashboardUrlState from './useDashboardUrlState';
import useLocale from '../../common/useLocale';
import useQuery from '../../api/useQuery';

type Event = GetEventRegistrationsStats['registrationCounts'][0]['event'];

interface Filter {
  event: string;
  tickets: string[];
}

interface FormState {
  filters: Filter[];
}

export interface CompareEventsModalProps {
  eventId: string;
  colors: string[];
  onSuccess?: () => void;
  onCancel?: () => void;
}

const CompareEventsModal = ({ eventId, colors, onSuccess, onCancel }: CompareEventsModalProps) => {
  const { t } = useTranslation();

  const { events } = useEvents();

  const [urlState, setUrlState] = useDashboardUrlState();

  const form = useForm<FormState>({
    values: {
      filters: [
        {
          event: eventId,
          tickets: urlState.tickets,
        },
        ...urlState.comparedEvents.map((param) => ({
          ...param,
          tickets: param.tickets || [],
        })),
        ...(urlState.comparedEvents.length === 0 ? [{ event: null, tickets: [] }] : []),
      ],
    },
  });

  const addEvent = () => form.set((values) => ({
    filters: [
      ...values.filters,
      { event: null, tickets: [] },
    ],
  }));

  const removeEvent = (index: number) => (
    form.set((values) => {
      const filters = [...values.filters];

      filters.splice(index, 1);

      return {
        ...values,
        filters,
      };
    })
  );

  const unsetEvent = (index: number) => (
    form.set((values) => {
      const filters = [...values.filters];

      filters[index] = {
        event: null,
        tickets: [],
      };

      return {
        ...values,
        filters,
      };
    })
  );

  const applyFilters = () => {
    setUrlState((urlState) => ({
      ...urlState,
      comparedEvents: form.values.filters.slice(1)
        .filter(({ event }) => !!event)
        .map((value) => ({ event: value.event, tickets: value.tickets })),
      tickets: form.values.filters[0].tickets,
    }));

    onSuccess?.();
  };

  const columns = form.values.filters.length;
  const maxWidth = columns * 350 + (columns - 1) * theme.gutter + 3 * theme.gutter;

  return (
    <UI.Modal maxWidth={maxWidth} close={onCancel}>
      <UI.Form onSubmit={() => applyFilters()}>
        <UI.FormGrid>
          <UI.Div>
            <UI.H4 sc={{ mb: 2 }}>
              {t('compare')}
            </UI.H4>
            <UI.InputDescription>
              {t('dashboard_registrations_select_events_description')}
            </UI.InputDescription>
          </UI.Div>
          <UI.FormGrid sc={{ columns: `repeat(${columns}, 350px)` }} style={{ overflow: 'hidden' }}>
            {form.values.filters.map((value, index) => (
              <UI.FadeIn key={index}>
                {value.event && (
                  <TicketList
                    form={form}
                    eventTitle={events.filter(({ id }) => id === value.event)[0]?.title}
                    index={index}
                    color={colors[index]}
                  />
                )}

                {!value.event ? (
                  <EventList form={form} events={events} index={index} color={colors[index]} />
                ) : null}

                <UI.FlexContainer
                  sc={{ mt: 1, color: 'gray.400', justifyContent: 'space-between' }}
                  style={{ lineHeight: '32px' }}
                >
                  {index === 0 && (
                    <UI.Div
                      sc={{ color: 'gray.400' }}
                      style={{ lineHeight: '28px' }}
                    >
                      {t('main_event')}
                    </UI.Div>
                  )}
                  {form.values.filters[index].event && index > 0 && (
                    <UI.Button onClick={() => unsetEvent(index)} sc={{ blank: true, size: 'xs', color: 'gray.500' }}>
                      <UI.Icon>
                        <ChevronLeft />
                      </UI.Icon>
                      {' '}
                      {t('back')}
                    </UI.Button>
                  )}
                  {index > 0 && (index > 1 || form.values.filters.length > 2) && (
                    <UI.Button
                      onClick={() => removeEvent(index)}
                      sc={{ blank: true, size: 'xs', color: 'gray.500' }}
                      style={{ marginLeft: 'auto' }}
                    >
                      <UI.Icon>
                        <Trash2 />
                      </UI.Icon>
                      {' '}
                      {t('remove')}
                    </UI.Button>
                  )}
                  {index > 0 && form.values.filters[index].event && form.values.filters.length < 3 && (
                    <UI.Button
                      onClick={() => addEvent()}
                      sc={{ blank: true, size: 'xs', color: 'gray.500', ml: 1 }}
                      style={{ marginLeft: 'auto' }}
                    >
                      <UI.Icon>
                        <Plus />
                      </UI.Icon>
                      {' '}
                      {t('add_event')}
                    </UI.Button>
                  )}
                </UI.FlexContainer>
              </UI.FadeIn>
            ))}
          </UI.FormGrid>

          <UI.HR />

          <UI.Div sc={{ textAlign: 'right' }}>
            <UI.Button
              type="submit"
              sc={{ brand: 'secondary' }}
            >
              {t('confirm')}
            </UI.Button>
          </UI.Div>
        </UI.FormGrid>
      </UI.Form>
    </UI.Modal>
  );
};

interface EventListProps {
  form: FormManager<FormState>;
  events: GetEventPickerEvents['project']['events']['data'];
  index: number;
  color: string;
}

const EventList = ({ form, events, index, color }: EventListProps) => {
  const { t } = useTranslation();
  const { formatDate, parseDate } = useLocale();

  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>();

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const reset = () => {
    setSearch('');

    if (searchRef.current) {
      searchRef.current.value = '';
    }
  };

  const selectEvent = (event: string) => {
    form.set((values) => {
      const filters = [...values.filters];
      filters[index] = { event, tickets: [] };
      return { filters };
    });
  };

  const visibleEvents = events.filter((event) => (
    event.title.toLowerCase().includes(search.toLowerCase())
  ));

  return (
    <UI.FormGrid>
      <UI.InputGroup style={{ minWidth: 0 }}>
        <UI.InputLabel style={{ color }}>
          <UI.Truncate>
            {index + 1}
            {'. '}
            {t('select_an_event')}
          </UI.Truncate>
        </UI.InputLabel>
        <UI.InputWrapper sc={{ mb: 1 }}>
          <UI.Span>
            <UI.Icon>
              <Search />
            </UI.Icon>
          </UI.Span>
          <UI.DebouncedInput
            type="text"
            value={search}
            onChange={handleSearch}
            placeholder={t('search')}
            ref={searchRef}
            sc={{ px: 5 }}
          />
          {search && (
            <UI.Span>
              <UI.A onClick={() => reset()} sc={{ color: 'gray.400' }}>
                <UI.Icon>
                  <XCircle />
                </UI.Icon>
              </UI.A>
            </UI.Span>
          )}
        </UI.InputWrapper>
        <UI.ScrollablePanel sc={{ padding: 1 }}>
          {visibleEvents.map((event, index) => (
            <EventButton
              type="button"
              onClick={() => selectEvent(event.id)}
              key={index}
            >
              <UI.Span sc={{ fontWeight: 500, block: true }}>{event.title}</UI.Span>
              <UI.Span sc={{ muted: true }}>
                {formatDate(parseDate(event.start_date, { timezone: 'UTC' }), { format: 'display_date' })}
              </UI.Span>
            </EventButton>
          ))}
        </UI.ScrollablePanel>
      </UI.InputGroup>
    </UI.FormGrid>
  );
};

interface TicketListProps {
  form: FormManager<FormState>;
  eventTitle?: string;
  index: number;
  color: string;
}

const TicketList = ({ form, eventTitle, index, color }: TicketListProps) => {
  const { t } = useTranslation();

  const eventId = form.values.filters[index].event;

  const { data } = useQuery(GetTicketListQuery, {
    variables: {
      event_id: eventId,
    },
  });

  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement>();

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
  };

  const reset = () => {
    setSearch('');

    if (searchRef.current) {
      searchRef.current.value = '';
    }
  };

  const event = data?.event;
  const tickets = event?.tickets || [];

  const visibleTickets = tickets.filter((ticket) => (
    form.values.filters[index].tickets.includes(ticket.id) || ticket.title.toLowerCase().includes(search.toLowerCase())
  ));

  const toggleAll = () => form.set(`filters.${index}.tickets`, []);

  return (
    <UI.FormGrid>
      <UI.InputGroup style={{ minWidth: 0 }}>
        <UI.InputLabel style={{ color }}>
          <UI.Truncate>
            {index + 1}
            {'. '}
            {eventTitle}
          </UI.Truncate>
        </UI.InputLabel>
        <UI.InputWrapper sc={{ mb: 1 }}>
          <UI.Span>
            <UI.Icon>
              <Search />
            </UI.Icon>
          </UI.Span>
          <UI.DebouncedInput
            type="text"
            value={search}
            onChange={handleSearch}
            placeholder={t('search')}
            ref={searchRef}
            sc={{ px: 5 }}
          />
          {search && (
            <UI.Span>
              <UI.A onClick={() => reset()} sc={{ color: 'gray.400' }}>
                <UI.Icon>
                  <XCircle />
                </UI.Icon>
              </UI.A>
            </UI.Span>
          )}
        </UI.InputWrapper>
        <UI.ScrollablePanel>
          {!data && <UI.PageLoader />}
          {data && (
            <UI.Checkbox
              checked={form.values.filters[index].tickets.length === 0}
              onChange={() => toggleAll()}
            >
              {t('all_tickets')}
            </UI.Checkbox>
          )}
          {visibleTickets.map((ticket) => (
            <UI.Checkbox
              {...form.checklist(`filters.${index}.tickets`, ticket.id)}
              checked={form.values.filters[index].tickets.includes(ticket.id)}
              key={ticket.id}
            >
              {ticket.title}
            </UI.Checkbox>
          ))}
        </UI.ScrollablePanel>
      </UI.InputGroup>
    </UI.FormGrid>
  );
};

const EventButton = styled.button`
  ${({ theme }) => css`
    background: transparent;
    border: none;
    text-align: left;
    padding: ${theme.gutter / 4}px;
    cursor: pointer;
    display: block;
    width: 100%;
    border-radius: ${theme.borderRadiuses.sm}px;

    &:disabled {
      cursor: default;
      opacity: 0.5;
    }

    &:hover:not(:disabled) {
      background: ${theme.colors.gray[100]};
    }
  `}
`;

interface UseCompareEventsModalOptions {
  event: Event;
  colors: string[];
}

export const useCompareEventsModal = ({ event, colors }: UseCompareEventsModalOptions) => {
  const [openModal, closeModal] = useModal(() => {
    const handleSuccess = () => {
      closeModal();
    };

    return (
      <CompareEventsModal eventId={event.id} colors={colors} onSuccess={handleSuccess} onCancel={closeModal} />
    );
  }, [event]);

  return [openModal, closeModal];
};

export default CompareEventsModal;
